import { useEffect, useRef } from "react";
import {
  useCaseCounts,
  useCaseDetail,
  useMarkCaseViewed,
} from "../../../api/caseQueries.js";
import { AsyncPanel } from "../../../ui/AsyncPanel.js";
import { PartsCaseInspector } from "../detail/PartsCaseInspector.js";
import { CaseListPane } from "./CaseListPane.js";
import { PartsShell } from "./PartsShell.js";
import { useWorkspaceUrlState } from "./useWorkspaceUrlState.js";

/**
 * The Parts desktop workspace (spec §10): three panels — navigation on the
 * left, the case list with counts, search and filters in the centre, and the
 * inspector for the selected case on the right — sized for 1440×900 and
 * 1280×800 (see `workspace.css`). Below 1280 the navigation folds into a
 * labelled menu and the list and inspector keep the width.
 *
 * All visible state is in the URL (`view`, `case`, `q`, `noEta`, `preRo`),
 * so a reload or a link from an arrival email restores the exact screen.
 * Selecting a row writes `case=<id>` and mounts the inspector for it — no
 * page navigation, the list stays where it was. Opening a case acknowledges
 * it as viewed through the read cursor the server returned with the detail
 * (spec §13), and only that cursor.
 *
 * The workspace sits in the `PartsShell`, which is Parts' own: any other
 * role that lands here is sent to the shared workspace its role starts on.
 * Order data is read-only here; the Parts actions live in the inspector
 * behind the server's permission flags.
 */

export function PartsWorkspacePage() {
  const url = useWorkspaceUrlState();
  const counts = useCaseCounts();

  return (
    <PartsShell url={url}>
      <CaseListPane url={url} counts={counts.data} />
      <aside className="workspace__inspector" aria-label="Case inspector">
        <InspectorPane caseId={url.state.caseId} />
      </aside>
    </PartsShell>
  );
}

/**
 * The right panel: a prompt while nothing is selected, `AsyncPanel` for the
 * loading, offline, not-found and denied states, and the inspector once the
 * server has answered. Acknowledges the case as viewed exactly once per
 * `(case, readThroughActivityEventId)` the server handed back.
 *
 * A refetch that fails after the case has loaded (React Query keeps `data`
 * and reports `status: "error"`) is a notice above the inspector, never the
 * offline panel in its place: the inspector — and any Verify or Reverse
 * dialog Parts is typing into — stays mounted with what loaded earlier.
 */
function InspectorPane({ caseId }: { caseId: string | null }) {
  const detail = useCaseDetail(caseId);
  const markViewed = useMarkCaseViewed();
  const acknowledged = useRef<string | null>(null);
  const mutate = markViewed.mutate;

  useEffect(() => {
    const data = detail.data;
    if (!caseId || !data || data.case.id !== caseId) return;
    const cursor = data.readThroughActivityEventId;
    if (!cursor) return;
    const key = `${caseId}:${cursor}`;
    if (acknowledged.current === key) return;
    acknowledged.current = key;
    mutate({ caseId, throughActivityEventId: cursor });
  }, [caseId, detail.data, mutate]);

  if (!caseId) {
    return (
      <AsyncPanel
        isEmpty
        emptyTitle="Select a case"
        emptyDescription="Choose an order from the list to see its parts, comments and activity here."
        className="inspector-pane__empty"
      >
        {null}
      </AsyncPanel>
    );
  }

  const loaded = detail.data !== undefined;
  return (
    <AsyncPanel
      isLoading={detail.isPending}
      error={loaded ? undefined : detail.error}
      refreshError={loaded ? detail.error : undefined}
      refreshTitle="Couldn't refresh this case."
      onRetry={() => {
        void detail.refetch();
      }}
      loadingLabel="Loading case…"
      className="inspector-pane__state"
    >
      {detail.data ? <PartsCaseInspector detail={detail.data} /> : null}
    </AsyncPanel>
  );
}
